import React from 'react';
import { NavLink } from 'react-router-dom';
import { usePermissions } from '../../contexts/PermissionsContext';
import { PortalModule } from '../Shared/ProtectedRoute';
import { ONBOARDING_URL } from '../../authConfig';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  disabled?: boolean;
  requiredModule?: PortalModule;
  // Application externe habillée comme le portail, ouverte dans le même onglet
  external?: boolean;
}

const navItems: NavItem[] = [
  {
    path: '/',
    label: 'Accueil',
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
      </svg>
    ),
  },
  {
    path: '/offboarding',
    label: 'Offboarding',
    requiredModule: 'offboarding',
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
      </svg>
    ),
  },
  {
    path: '/esl-solum',
    label: 'Étiquettes SOLUM',
    requiredModule: 'eslSolum',
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
      </svg>
    ),
  },
  {
    path: '/odoo-images',
    label: 'Export images Odoo',
    requiredModule: 'odooImages',
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
      </svg>
    ),
  },
  {
    path: ONBOARDING_URL,
    label: 'Onboarding / Offboarding RH',
    requiredModule: 'onboarding',
    external: true,
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
      </svg>
    ),
  },
  {
    path: '/calendar',
    label: 'Calendrier',
    disabled: true,
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
      </svg>
    ),
  },
  {
    path: '/absences',
    label: 'Absences',
    disabled: true,
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
      </svg>
    ),
  },
];

const Sidebar: React.FC = () => {
  const { permissions } = usePermissions();

  const visibleItems = navItems.filter((item) => {
    if (item.disabled) return permissions.admin; // "Bientôt" réservé aux admins
    if (!item.requiredModule) return true;
    return permissions[item.requiredModule];
  });

  return (
    <aside className="w-56 bg-white border-r border-ms-gray-200 fixed left-0 top-14 bottom-0 overflow-y-auto">
      <nav className="py-4">
        <ul className="space-y-1 px-3">
          {visibleItems.map((item) => (
            <li key={item.path}>
              {item.disabled ? (
                <div className="flex items-center gap-3 px-3 py-2.5 text-ms-gray-400 cursor-not-allowed rounded-md">
                  {item.icon}
                  <span className="text-sm">{item.label}</span>
                  <span className="ml-auto text-xs bg-ms-gray-100 text-ms-gray-400 px-1.5 py-0.5 rounded">
                    Bientôt
                  </span>
                </div>
              ) : item.external ? (
                <a
                  href={item.path}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors text-sm text-ms-gray-600 hover:bg-ms-gray-100 hover:text-ms-gray-800"
                >
                  {item.icon}
                  <span>{item.label}</span>
                </a>
              ) : (
                <NavLink
                  to={item.path}
                  end={item.path === '/'}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors text-sm ${
                      isActive
                        ? 'bg-ms-blue-light text-ms-blue font-medium'
                        : 'text-ms-gray-600 hover:bg-ms-gray-100 hover:text-ms-gray-800'
                    }`
                  }
                >
                  {item.icon}
                  <span>{item.label}</span>
                </NavLink>
              )}
            </li>
          ))}
        </ul>
      </nav>

      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-ms-gray-200">
        <p className="text-xs text-ms-gray-400">IT Portal v1.1.0</p>
      </div>
    </aside>
  );
};

export default Sidebar;
